import PropTypes from 'prop-types';
import { useMemo } from 'react';
import Dotdotdot from 'react-dotdotdot';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { focusOutline } from '@hivebrite/design-system/Theme';

import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

const ButtonCard = ({
  dataTestId,
  title,
  to,
  ariaLabel,
  clamp,
  height,
  IconComponent,
  isDisabled,
  onClick,
}) => {
  const isLink = !!to;
  const navigate = useNavigate();
  const linkProps = useMemo(
    () =>
      isLink
        ? {
            as: 'a',
            href: to,
            onClick: (e) => {
              onClick?.(e);
              if (!e.isDefaultPrevented()) {
                e.preventDefault();
                navigate(to);
              }
            },
          }
        : {},
    [to, onClick],
  );
  const buttonProps = useMemo(
    () =>
      isDisabled || isLink
        ? linkProps
        : {
            role: 'button',
            tabIndex: 0,
            onClick,
            onKeyPress: onEnterOrSpacePress(onClick),
          },
    [isDisabled, onClick],
  );

  return (
    <StyledCard
      data-testid={dataTestId}
      aria-label={ariaLabel}
      height={height}
      disabled={isDisabled}
      canBeHovered
      {...buttonProps}
    >
      <StyledName as="span">
        <Dotdotdot tagName="span" clamp={clamp} useNativeClamp>
          {title}
        </Dotdotdot>
      </StyledName>
      {IconComponent && (
        <WrapperIcon height={{ _: '30px', l: '60px' }} aria-hidden>
          <IconComponent width="100%" height="100%" />
        </WrapperIcon>
      )}
    </StyledCard>
  );
};

ButtonCard.propTypes = {
  dataTestId: PropTypes.string,
  title: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  // @see https://github.com/CezaryDanielNowak/React-dotdotdot#dotdotdot-props
  clamp: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.oneOf(['auto']),
  ]),
  IconComponent: PropTypes.elementType,
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func,
};

ButtonCard.defaultProps = {
  height: {
    _: '177px',
    m: '180px',
    l: '242px',
    xl: '225px',
  },
  clamp: 3,
};

export default ButtonCard;

const StyledName = styled(Box).attrs({
  font: 'body-semibold-asphalt',
  textAlign: 'center',
  px: 3,
  pt: 6,
})`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  justify-content: center;
  // Needed to change the alpha on primary color only on title & icons
  color: ${({ theme }) => theme.colors.primary.replace(/0\..+/g, '1)')};
`;

const WrapperIcon = styled(Box)`
  color: ${({ theme }) => theme.colors.primary.replace(/0\..+/g, '1)')};
`;

const StyledCardDisabled = css`
  cursor: default;

  & ${StyledName} {
    opacity: 0.6;
  }

  & ${WrapperIcon} {
    opacity: 0.6;
  }
`;

const StyledCardRegular = css`
  cursor: pointer;

  &:hover,
  &:focus {
    & ${StyledName} {
      color: ${({ theme }) => theme.colors.primaryDark.replace(/0\..+/g, '1)')};
    }

    & ${WrapperIcon} {
      color: ${({ theme }) => theme.colors.primaryDark.replace(/0\..+/g, '1)')};
    }
  }
`;

const StyledCard = styled(Card)
  .attrs({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    px: 3,
    pt: 6,
    pb: {
      _: 5,
      l: 0,
    },
  })
  .withConfig({
    shouldForwardProp: (prop) => !['disabled'].includes(prop),
  })`
  position: relative;
  outline: none;
  ${focusOutline}

  ${({ disabled }) => (disabled ? StyledCardDisabled : StyledCardRegular)}
`;
